/**
 * <charm-picker> — PDP charm rail + detail popup.
 *
 * Ticking a `.charm-checkbox` is the add-to-cart integration: theme.js
 * `ProductForm._onSubmit` collects every checked one on the page and appends
 * those variant ids to the cart payload.
 */
(function () {
  if (window.customElements.get('charm-picker')) return;

  var MONEY_PLACEHOLDER = /\{\{\s*(\w+)\s*\}\}/;

  function formatWithDelimiters(cents, precision, thousands, decimal) {
    var number = Number(cents);
    if (isNaN(number)) return '0';

    var parts = (number / 100).toFixed(precision).split('.');
    var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);

    return whole + (parts[1] ? decimal + parts[1] : '');
  }

  function formatMoney(cents) {
    var settings = (window.themeVariables && window.themeVariables.settings) || {};
    var format = settings.moneyFormat || '{{ amount }}';
    var placeholder = format.match(MONEY_PLACEHOLDER);

    if (!placeholder) return format;

    var value;

    switch (placeholder[1]) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', '.');
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ', '.');
        break;
      case 'amount_no_decimals_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 0, "'", '.');
        break;
      default:
        value = formatWithDelimiters(cents, 2, ',', '.');
    }

    return format.replace(MONEY_PLACEHOLDER, value);
  }

  function absoluteUrl(url) {
    if (!url) return '';
    return url.indexOf('//') === 0 ? 'https:' + url : url;
  }

  var CharmPicker = class extends HTMLElement {
    connectedCallback() {
      this._abort = new AbortController();

      var signal = this._abort.signal;

      this.rail = this.querySelector('.charm-picker__wrapper');
      this.popup = this.querySelector('.charm-popup');
      this.selectedCount = this.querySelector('.selected-count');
      this.selectedTotal = this.querySelector('.selected-total');

      if (!this.rail || !this.popup) return;

      this.gallery = this.popup.querySelector('.charm-popup-gallery');
      this.dots = this.popup.querySelector('.charm-popup-dots');
      this.thumbs = this.popup.querySelector('.charm-popup-thumbs');
      this.popupTitle = this.popup.querySelector('.charm-popup-title');
      this.popupPrice = this.popup.querySelector('.popup-price');
      this.popupComparePrice = this.popup.querySelector('.popup-compare-price');

      this.currentCard = null;
      this._dragging = false;
      this._dragStartX = 0;
      this._dragScrollLeft = 0;
      this._dotsFrame = null;

      this.addEventListener('click', this._onClick.bind(this), { signal: signal });
      this.addEventListener('change', this._onChange.bind(this), { signal: signal });

      this.rail.addEventListener('mousedown', this._onDragStart.bind(this), { signal: signal });
      this.rail.addEventListener('mousemove', this._onDragMove.bind(this), { signal: signal });
      this.rail.addEventListener('mouseup', this._onDragEnd.bind(this), { signal: signal });
      this.rail.addEventListener('mouseleave', this._onDragEnd.bind(this), { signal: signal });

      this.gallery.addEventListener('scroll', this._onGalleryScroll.bind(this), {
        passive: true,
        signal: signal
      });

      // variant:change is dispatched on the product form, which is a sibling of
      // this element, so it is caught on document and filtered by form id.
      document.addEventListener('variant:change', this._onVariantChange.bind(this), {
        signal: signal
      });

      this._updateSummary();
    }

    disconnectedCallback() {
      if (this._abort) this._abort.abort();
      if (this._dotsFrame) cancelAnimationFrame(this._dotsFrame);

      this._dotsFrame = null;
      this.currentCard = null;
    }

    get productForm() {
      var container = this.closest('.product-form');
      var form = container && container.querySelector('form[is="product-form"]');

      if (!form && this.dataset.formId) {
        form = document.getElementById(this.dataset.formId);
      }

      return form || document.querySelector('form[is="product-form"]');
    }

    get cards() {
      return Array.prototype.slice.call(this.querySelectorAll('.charm-card'));
    }

    /* ------------------------------------------------------------ events */

    _onClick(event) {
      var target = event.target;

      if (target.closest('.charm-title')) {
        // Cancels the surrounding <label> activating the checkbox.
        event.preventDefault();
        this._openPopup(target.closest('.charm-card'));
        return;
      }

      if (target.closest('.charm-popup-add')) {
        this._addCurrentCharm();
        return;
      }

      if (target.closest('.charm-popup-close') || target.closest('.charm-popup-overlay')) {
        this._closePopup();
        return;
      }

      if (target.matches('.charm-popup-thumbs img')) {
        var card = this.cards[Number(target.dataset.charmIndex)];
        if (card) this._showCharm(card);
        return;
      }

      if (target.matches('.charm-popup-dot')) {
        this.gallery.scrollTo({
          left: this.gallery.clientWidth * Number(target.dataset.charmSlide),
          behavior: 'smooth'
        });
      }
    }

    _onChange(event) {
      if (event.target.matches('.charm-card .charm-checkbox')) this._updateSummary();
    }

    _onVariantChange(event) {
      var variant = event.detail && event.detail.variant;
      if (!variant) return;

      var formId = event.detail.formId;
      if (formId && this.dataset.formId && formId !== this.dataset.formId) return;

      var json = this.querySelector('[data-knob-images="' + variant.id + '"]');
      if (!json) return;

      var images;

      try {
        images = JSON.parse(json.textContent);
      } catch (e) {
        return;
      }

      if (!images) return;

      this.querySelectorAll('.charm-card__image').forEach(function (wrapper, index) {
        var image = images[index];

        wrapper.innerHTML = image
          ? '<img src="' +
            absoluteUrl(image) +
            '" width="300" height="300" alt="" loading="lazy" decoding="async">'
          : '';
      });
    }

    /* ------------------------------------------------------------- popup */

    _openPopup(card) {
      if (!card) return;

      this._buildThumbs(card);
      this._showCharm(card);
      this.popup.classList.add('is-visible');
    }

    _closePopup() {
      this.popup.classList.remove('is-visible');
    }

    _showCharm(card) {
      this.currentCard = card;

      this._renderGallery(card);

      var price = Number(card.dataset.price);
      var comparePrice = Number(card.dataset.comparePrice);

      this.popupTitle.textContent = card.dataset.title;
      this.popupPrice.textContent = formatMoney(price);

      if (comparePrice > price) {
        this.popupComparePrice.textContent = formatMoney(comparePrice);
        this.popupComparePrice.style.display = 'inline';
      } else {
        this.popupComparePrice.textContent = '';
        this.popupComparePrice.style.display = 'none';
      }

      var activeIndex = String(this.cards.indexOf(card));

      this.thumbs.querySelectorAll('img').forEach(function (img) {
        img.classList.toggle('active', img.dataset.charmIndex === activeIndex);
      });
    }

    _addCurrentCharm() {
      if (!this.currentCard) return;

      var checkbox = this.currentCard.querySelector('.charm-checkbox');
      if (checkbox) checkbox.checked = true;

      this._updateSummary();
      this._closePopup();

      var form = this.productForm;
      if (form) form.requestSubmit();
    }

    _updateSummary() {
      var count = 0;
      var total = 0;

      this.cards.forEach(function (card) {
        var checkbox = card.querySelector('.charm-checkbox');

        if (checkbox && checkbox.checked) {
          count++;
          total += Number(card.dataset.price) || 0;
        }
      });

      if (this.selectedCount) this.selectedCount.textContent = count;
      if (this.selectedTotal) this.selectedTotal.innerHTML = formatMoney(total);
    }

    /* ----------------------------------------------------------- gallery */

    _renderGallery(card) {
      var media;

      // data-media JSON is parsed here, never on page load.
      try {
        media = JSON.parse(card.dataset.media || '[]');
      } catch (e) {
        media = [];
      }

      var slides = document.createDocumentFragment();
      var dots = document.createDocumentFragment();
      var slideIndex = 0;

      media.forEach(function (item) {
        var slide = document.createElement('div');
        slide.className = 'charm-popup__slide';

        if (item.media_type === 'image') {
          slide.innerHTML =
            '<img src="' +
            absoluteUrl(item.src) +
            '" width="' +
            (item.width || 600) +
            '" height="' +
            (item.height || 600) +
            '" alt="" draggable="false" loading="lazy" decoding="async">';
        } else if (item.media_type === 'video' && item.sources && item.sources.length) {
          slide.innerHTML =
            '<video playsinline controls preload="metadata" width="600" height="600">' +
            '<source src="' +
            absoluteUrl(item.sources[0].url) +
            '" type="video/mp4"></video>';
        } else {
          return;
        }

        slides.appendChild(slide);

        var dot = document.createElement('span');
        dot.className = 'charm-popup-dot' + (slideIndex === 0 ? ' active' : '');
        dot.dataset.charmSlide = slideIndex;
        dots.appendChild(dot);

        slideIndex++;
      });

      this.gallery.replaceChildren(slides);
      this.dots.replaceChildren(dots);
      this.gallery.scrollLeft = 0;
    }

    _onGalleryScroll() {
      if (this._dotsFrame) return;

      this._dotsFrame = requestAnimationFrame(
        function () {
          this._dotsFrame = null;

          var width = this.gallery.clientWidth;
          if (!width) return;

          var index = Math.round(this.gallery.scrollLeft / width);

          this.dots.querySelectorAll('.charm-popup-dot').forEach(function (dot, i) {
            dot.classList.toggle('active', i === index);
          });
        }.bind(this)
      );
    }

    _buildThumbs(activeCard) {
      var fragment = document.createDocumentFragment();

      this.cards.forEach(function (card, index) {
        var img = document.createElement('img');

        img.src = card.dataset.image;
        img.width = 50;
        img.height = 50;
        img.alt = '';
        img.loading = 'lazy';
        img.decoding = 'async';
        img.dataset.charmIndex = index;

        if (card === activeCard) img.classList.add('active');

        fragment.appendChild(img);
      });

      this.thumbs.replaceChildren(fragment);
    }

    /* -------------------------------------------------- drag-to-scroll */

    _onDragStart(event) {
      this._dragging = true;
      this.rail.classList.add('dragging');
      this._dragStartX = event.pageX - this.rail.offsetLeft;
      this._dragScrollLeft = this.rail.scrollLeft;
    }

    _onDragMove(event) {
      if (!this._dragging) return;

      event.preventDefault();

      var walk = (event.pageX - this.rail.offsetLeft - this._dragStartX) * 2;
      this.rail.scrollLeft = this._dragScrollLeft - walk;
    }

    _onDragEnd() {
      this._dragging = false;
      this.rail.classList.remove('dragging');
    }
  };

  window.customElements.define('charm-picker', CharmPicker);
})();
